import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, ExternalLink, Loader2, Sparkles } from "lucide-react";
import { aiModels, aiProviderModels, saveKeys } from "../lib/api";

const errText = (e, fallback) => { const d = e?.response?.data?.detail; return typeof d === "string" ? d : fallback; };

// Pick which AI writes. "Auto" tries the free models first and moves on by itself when one is out of free quota.
// A provider that isn't set up shows a short way to add its key right here.
export default function ModelPicker({ value, onChange, onChanged, compact = false }) {
  const [data, setData] = useState(null);
  const [models, setModels] = useState([]);
  const [loadingModels, setLoadingModels] = useState(false);
  const [key, setKey] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => aiModels().then(d => { setData(d); onChanged?.(); }).catch(() => {}), []);  // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);

  const provider = data?.providers.find(p => p.id === value.provider);
  useEffect(() => {
    setModels([]);
    if (!provider || !provider.configured) return undefined;
    let live = true;
    setLoadingModels(true);
    aiProviderModels(provider.id).then(r => { if (live) setModels(r.models); }).catch(() => {}).finally(() => live && setLoadingModels(false));
    return () => { live = false; };
  }, [provider?.id, provider?.configured]);  // eslint-disable-line react-hooks/exhaustive-deps

  if (!data) return <div className="model-picker skeleton-line" />;
  const first = data.providers.find(p => p.id === data.order[0]);
  const free = data.providers.filter(p => p.free && !p.local), local = data.providers.filter(p => p.local), paid = data.providers.filter(p => !p.free);
  const opt = (p) => <option key={p.id} value={p.id}>{p.label}{p.configured ? "" : " — needs a key"}</option>;

  const saveKey = async () => {
    setSaving(true);
    try {
      const k = provider.key_env;
      await saveKeys(provider.local ? { OLLAMA_BASE_URL: key.trim() || "http://localhost:11434/v1" } : { [k]: key.trim() });
      setKey(""); await load(); toast.success(`${provider.label} is ready`);
    } catch (e) { toast.error(errText(e, "Could not save the key")); }
    finally { setSaving(false); }
  };

  return <div className={`model-picker ${compact ? "compact" : ""}`} data-testid="model-picker">
    <label>AI model
      <select value={value.provider} onChange={e => onChange({ provider: e.target.value, model: "" })} data-testid="model-provider">
        <option value="auto">Auto — free models first</option>
        <optgroup label="Free">{free.map(opt)}</optgroup>
        {local.length > 0 && <optgroup label="On this computer (free, private)">{local.map(opt)}</optgroup>}
        <optgroup label="Paid">{paid.map(opt)}</optgroup>
      </select>
    </label>
    {value.provider === "auto" && <small className="muted">
      {first ? <><Sparkles size={11} /> Will use <b>{first.label}</b> · {first.model}{first.free ? " · free" : ""}. If its free limit runs out, the next set-up model takes over.</>
        : "No AI is set up yet. Pick a free one below and paste its key."}
    </small>}
    {value.provider === "auto" && !first && <div className="model-setup">
      <b>Start free with Google Gemini</b>
      <ol><li><a href={data.providers[0].key_url} target="_blank" rel="noreferrer">Open Google AI Studio <ExternalLink size={11} /></a> and click <b>Create API key</b>.</li><li>Paste the key here.</li></ol>
      <div className="model-key"><input type="password" value={key} onChange={e => setKey(e.target.value)} placeholder="Paste your Gemini key" autoComplete="off" />
        <button className="primary-button" disabled={!key.trim() || saving} onClick={async () => { setSaving(true); try { await saveKeys({ GEMINI_API_KEY: key.trim() }); setKey(""); await load(); toast.success("Gemini is ready"); } catch (e) { toast.error(errText(e, "Could not save the key")); } finally { setSaving(false); } }}>{saving ? <Loader2 size={13} className="spin" /> : <Check size={13} />} Save</button></div>
    </div>}
    {provider && provider.configured && <label>Model
      <select value={value.model || provider.model} onChange={e => onChange({ ...value, model: e.target.value })} data-testid="model-name">
        {(models.length ? models : [{ id: provider.model, free: provider.free }]).map(m => <option key={m.id} value={m.id}>{m.id}{m.free ? " · free" : ""}</option>)}
      </select>
      {loadingModels && <small className="muted"><Loader2 size={11} className="spin" /> Loading this key's models…</small>}
    </label>}
    {provider && !provider.configured && <div className="model-setup">
      <small className="muted">{provider.note}</small>
      <div className="model-key">
        <input type={provider.local ? "text" : "password"} value={key} onChange={e => setKey(e.target.value)} autoComplete="off"
          placeholder={provider.local ? "http://localhost:11434/v1 (leave blank for the default)" : `Paste your ${provider.label} key`} />
        <button className="primary-button" disabled={(!provider.local && !key.trim()) || saving} onClick={saveKey}>{saving ? <Loader2 size={13} className="spin" /> : <Check size={13} />} {provider.local ? "Turn on" : "Save"}</button>
      </div>
      <a href={provider.key_url} target="_blank" rel="noreferrer" className="key-get">{provider.local ? "Get Ollama" : "Get a key"} <ExternalLink size={11} /></a>
    </div>}
    {provider && provider.configured && provider.note && <small className="muted">{provider.note}</small>}
  </div>;
}
